import { useUpdateStaffActionPermissionsMutation } from "@src/features/app/top-nav/SetActionPermissions.generated";
import { Button } from "@src/lib/ui/Button";
import Spinner from "@src/lib/ui/Spinner";
import Text from "@src/lib/ui/Text";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import useUser from "@src/features/app/hooks/useUser";
import { GraphqlOperations, UserType } from "@src/types.generated";
import {
  AdminToolsPanel,
  AdminToolsRowList,
  AdminToolsToggleRow,
} from "@src/features/app/top-nav/AdminToolsLayout";
import {
  useUserActionPermissionDetails,
  UserActionPermissionsKey,
} from "@src/features/roles/utils/actionPermissions";
import gql from "graphql-tag";
import { startCase, toLower } from "lodash";
import { FC, useCallback, useMemo } from "react";
import { useUpdateUserMutation } from "@src/features/users/user-details/UserDetails.generated";

const SetActionPermissions: FC = () => {
  const { isStaff } = useUser();
  const { userActionPermissions, loading } = useActionPermissions();
  const permissionDetails = useUserActionPermissionDetails();
  const [updateStaffPermissions] = useUpdateStaffActionPermissionsMutation({
    refetchQueries: [GraphqlOperations.Query.UserActionPermissions],
  });

  const updatePermission = useCallback(
    (field: UserActionPermissionsKey, currentValue: boolean) => {
      updateStaffPermissions({
        variables: {
          input: {
            [field]: {
              value: !currentValue,
            },
          },
        },
      });
    },
    [updateStaffPermissions],
  );
  const permissionToggleRows = useMemo(() => {
    if (!userActionPermissions) return null;
    return Object.entries(userActionPermissions).map(
      ([_permissionKey, selected]) => {
        const permissionKey = _permissionKey as UserActionPermissionsKey;
        return (
          <AdminToolsToggleRow
            key={permissionKey}
            title={permissionDetails[permissionKey].name}
            checked={selected}
            onCheckedChange={() => updatePermission(permissionKey, selected)}
          />
        );
      },
    );
  }, [userActionPermissions, updatePermission, permissionDetails]);
  if (!isStaff) {
    return null;
  }
  return (
    <AdminToolsPanel>
      {loading && userActionPermissions == null ? (
        <div className="px-6 py-4">
          <Spinner />
        </div>
      ) : (
        <AdminToolsRowList>{permissionToggleRows}</AdminToolsRowList>
      )}
    </AdminToolsPanel>
  );
};

export const SetUserType: FC = () => {
  const { isStaff } = useUser();
  if (!isStaff) {
    return null;
  }
  return (
    <div className="flex items-center gap-2.5">
      <Text variant="helper-title" className="text-text-secondary">
        User type
      </Text>
      <div className="flex flex-wrap gap-1.5">
        <UpdateUserTypeButton userType={UserType.Admin} />
        <UpdateUserTypeButton userType={UserType.Manager} />
        <UpdateUserTypeButton userType={UserType.Employee} />
      </div>
    </div>
  );
};

type UpdateUserTypeButtonProps = {
  userType: UserType;
};

const UpdateUserTypeButton: FC<UpdateUserTypeButtonProps> = ({ userType }) => {
  const { user } = useUser();
  const [updateUserType, { loading: saveUserTypeLoading }] =
    useUpdateUserMutation({
      refetchQueries: [
        GraphqlOperations.Query.UserActionPermissions,
        GraphqlOperations.Query.Init,
      ],
    });
  return (
    <Button
      size="sm"
      variant={user?.userType === userType ? "default" : "outline"}
      loading={saveUserTypeLoading}
      onClick={() => {
        if (!user?.id || !user?.userType || user.userType === userType) {
          return;
        }
        updateUserType({
          variables: {
            id: user.id,
            input: {
              userType: { value: userType },
            },
          },
        });
      }}
    >
      {startCase(toLower(userType))}
    </Button>
  );
};

export default SetActionPermissions;

gql`
  mutation updateStaffActionPermissions($input: UserActionPermissionsInput!) {
    updateUserActionPermissions(input: $input) {
      success
    }
  }
`;
